import type { ApiErrorBody } from './types'

/**
 * Единственная точка выхода в сеть.
 *
 * Все запросы идут через неё, и на то есть причина: разбор ошибок,
 * передача куки и обработка отвалившейся сессии должны быть одинаковыми
 * везде. Стоит разрешить компонентам звать fetch напрямую — и половина
 * экранов начнёт по-своему показывать ошибку, а один из них забудет
 * credentials и будет молча получать 401.
 */

const BASE = '/api'

/**
 * Какой бот компании сейчас правится. Сервер читает заголовок на
 * анкете, сценарии, фразах, предпросмотре и тестовом звонке; без него —
 * основной бот, ровно как до появления ботов.
 *
 * Модульная переменная, а не параметр каждого запроса: иначе бот
 * пришлось бы протаскивать через все хуки, и один забытый вызов молча
 * правил бы основного.
 */
let currentBot: number | null = null

export function setRequestBot(id: number | null) {
  currentBot = id
}

/**
 * Ошибка API с разобранным телом.
 *
 * Отдельный класс, а не строка: экрану нужны и код (401 обрабатывается
 * иначе, чем 400), и поле формы, чтобы подсветить конкретный ввод.
 */
export class ApiError extends Error {
  readonly status: number
  readonly field?: string

  constructor(status: number, message: string, field?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.field = field
  }

  /** Сессия кончилась — надо отправить на вход, а не показывать ошибку. */
  get isUnauthorized(): boolean {
    return this.status === 401
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options

  let response: Response
  try {
    response = await fetch(BASE + path, {
      method,
      // Кука сессии HttpOnly, и без этого флага браузер её не приложит.
      // Умолчание fetch — 'same-origin', чего хватает, но написано явно:
      // при переезде API на другой домен молчаливая потеря куки выглядит
      // как «сервер разлогинивает», и ищут её долго.
      credentials: 'include',
      headers: {
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(currentBot === null ? {} : { 'X-Bot-Id': String(currentBot) }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    })
  } catch (cause) {
    // Сеть отвалилась: сюда же попадает отмена запроса при уходе со
    // страницы, поэтому её отделяем — показывать «нет связи» человеку,
    // который просто перешёл в другой раздел, незачем.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError(0, 'Нет связи с сервером')
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()

  if (!response.ok) {
    throw new ApiError(response.status, ...parseError(text, response.status))
  }

  if (!text) return undefined as T

  try {
    return JSON.parse(text) as T
  } catch {
    throw new ApiError(response.status, 'Сервер вернул неразборчивый ответ')
  }
}

/**
 * Достаёт сообщение из тела ошибки.
 *
 * Тело может оказаться и не JSON — например, когда до Go не дошло и
 * ответил прокси своей HTML-страницей. Показывать её человеку нельзя, но
 * и молчать нельзя тоже: подставляем текст по коду.
 */
function parseError(text: string, status: number): [string, string?] {
  try {
    const body = JSON.parse(text) as ApiErrorBody
    if (body?.error) return [body.error, body.field]
  } catch {
    /* не JSON — ниже подставим текст по коду */
  }

  if (status === 401) return ['Нужно войти заново']
  if (status === 403) return ['Нет доступа']
  if (status === 404) return ['Не найдено']
  if (status >= 500) return ['Сервер не отвечает. Попробуйте ещё раз']
  return ['Что-то пошло не так']
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
